import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import OneSignal from 'react-onesignal';
import { toast } from 'sonner';

const PushNotificationContext = createContext(null);

// Dissociate this browser/device from the current user's OneSignal external
// id so the next user on this device never receives their notifications.
// Safe to call even if OneSignal was never initialized.
export const oneSignalSafeLogout = async () => {
  try {
    await OneSignal.logout();
  } catch (e) {
    // SDK not initialized / unsupported browser — nothing to unlink
  }
};

export const usePushNotifications = () => {
  const context = useContext(PushNotificationContext);
  if (!context) {
    throw new Error('usePushNotifications must be used within PushNotificationProvider');
  }
  return context;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper: get auth token for either owner or technician
const getAuthToken = () => localStorage.getItem('token') || localStorage.getItem('tech_token');

export function PushNotificationProvider({ children, user }) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [permission, setPermission] = useState('default');
  const [onesignalConfig, setOnesignalConfig] = useState(null);
  const [isNativeApp, setIsNativeApp] = useState(false);

  // Detect native app environment
  useEffect(() => {
    if (window.FieldzNative?.isNativeApp) {
      setIsNativeApp(true);
    }
    const handler = () => setIsNativeApp(true);
    window.addEventListener('fieldzNativeReady', handler);
    return () => window.removeEventListener('fieldzNativeReady', handler);
  }, []);

  // Register Expo push token received from native app bridge
  const registerExpoToken = useCallback(async (expoToken) => {
    if (!expoToken) return;
    try {
      const token = getAuthToken();
      if (!token) {
        // Auth not ready — retry after delay
        console.log('Auth token not ready, retrying Expo token registration in 2s');
        setTimeout(() => registerExpoToken(expoToken), 2000);
        return;
      }
      const res = await fetch(`${API}/push/expo-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          expo_push_token: expoToken,
          device_type: 'mobile',
          platform: /iPhone|iPad/i.test(navigator.userAgent) ? 'ios' : 'android',
        }),
      });
      if (res.ok) {
        setIsSubscribed(true);
        setPermission('granted');
        console.log('Expo push token registered from native bridge');
      } else {
        const errData = await res.json().catch(() => ({}));
        console.error('Expo token registration failed:', res.status, errData);
      }
    } catch (err) {
      console.error('Failed to register Expo token:', err);
    }
  }, []);

  // Listen for messages from native React Native WebView bridge
  useEffect(() => {
    const handleNativeMessage = (event) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.type === 'expoPushToken' && data?.token) {
          registerExpoToken(data.token);
        }
      } catch { /* ignore non-JSON messages */ }
    };

    // Check if native app already injected the token
    if (window.FieldzNative?.expoPushToken) {
      registerExpoToken(window.FieldzNative.expoPushToken);
    }

    window.addEventListener('message', handleNativeMessage);
    // React Native WebView also uses document-level messages
    document.addEventListener('message', handleNativeMessage);

    // Periodic re-check: native app might inject token after mount
    const checkInterval = setInterval(() => {
      if (window.FieldzNative?.expoPushToken && !isSubscribed) {
        registerExpoToken(window.FieldzNative.expoPushToken);
      }
    }, 5000);

    return () => {
      window.removeEventListener('message', handleNativeMessage);
      document.removeEventListener('message', handleNativeMessage);
      clearInterval(checkInterval);
    };
  }, [registerExpoToken, isSubscribed]);

  // Fetch subscription status from backend (covers both web & mobile)
  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API}/push/subscription`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        if (data.is_subscribed) {
          setIsSubscribed(true);
          setPermission('granted');
        }
      }
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
    }
  }, []);

  // Fetch OneSignal config from backend
  const fetchConfig = useCallback(async () => {
    try {
      const response = await fetch(`${API}/push/config`);
      if (response.ok) {
        const config = await response.json();
        setOnesignalConfig(config);
        return config;
      }
    } catch (error) {
      console.error('Failed to fetch OneSignal config:', error);
    }
    return null;
  }, []);

  // Initialize OneSignal (web only — won't work inside mobile WebView)
  const initializeOneSignal = useCallback(async () => {
    if (isInitialized || typeof window === 'undefined') return;

    // In native app, push is handled by the native shell via Expo
    if (isNativeApp || window.FieldzNative?.isNativeApp) {
      // Still fetch config so isConfigured is set for the UI
      const config = await fetchConfig();
      setIsInitialized(true);
      setIsLoading(false);
      await fetchSubscriptionStatus();
      // Try to register the token if available
      if (window.FieldzNative?.expoPushToken) {
        await registerExpoToken(window.FieldzNative.expoPushToken);
      }
      return;
    }

    try {
      const config = await fetchConfig();
      if (!config?.app_id || !config?.configured) {
        console.log('OneSignal not configured');
        setIsLoading(false);
        return;
      }

      // Guard against double initialization (e.g., redirect between layouts)
      try {
        await OneSignal.init({
          appId: config.app_id,
          allowLocalhostAsSecureOrigin: process.env.NODE_ENV === 'development',
          notifyButton: {
            enable: false,
          },
          serviceWorkerParam: {
            scope: '/',
          },
          serviceWorkerPath: '/OneSignalSDKWorker.js',
        });
      } catch (initErr) {
        // "SDK already initialized" is safe to ignore — just re-login
        if (!initErr?.message?.includes('already initialized')) {
          throw initErr;
        }
      }

      setIsInitialized(true);

      // Login user to OneSignal (links external_id for targeting)
      if (user?.user_id) {
        await OneSignal.login(user.user_id);
      }

      const currentPermission = await OneSignal.Notifications.permission;
      setPermission(currentPermission ? 'granted' : 'default');

      const subscription = await OneSignal.User.PushSubscription;
      if (subscription) {
        setIsSubscribed(subscription.optedIn || false);
        // If permission granted but not opted in, auto-opt-in
        if (currentPermission && !subscription.optedIn) {
          try {
            await OneSignal.User.PushSubscription.optIn();
            setIsSubscribed(true);
          } catch { /* opt-in failed, user can do manually */ }
        }
        // Store subscription on backend if opted in
        if ((subscription.optedIn || currentPermission) && subscription.id) {
          storeSubscription(subscription.id);
        }
      }

      OneSignal.User.PushSubscription.addEventListener('change', (event) => {
        setIsSubscribed(event.current.optedIn || false);
        if (event.current.optedIn && event.current.id) {
          storeSubscription(event.current.id);
        }
      });

      OneSignal.Notifications.addEventListener('click', (event) => {
        const data = event.notification.additionalData || event.notification.data || {};
        const url = event.notification.launchURL || event.notification.url || data.url;
        if (url) {
          // Handle relative and absolute URLs
          if (url.startsWith('/')) {
            window.location.href = url;
          } else if (url.startsWith('http')) {
            window.location.href = url;
          }
        } else if (data.type === 'new_message' && data.conversation_id) {
          window.location.href = `/dashboard/messages?conversation=${data.conversation_id}`;
        } else if ((data.type === 'job_assignment' || data.type === 'status_update') && data.job_id) {
          window.location.href = `/dashboard/jobs?view=${data.job_id}`;
        }
      });

      // Foreground notifications — forward to the app shell (DashboardLayout)
      // through ONE deduplicated channel so a single push never produces
      // overlapping sounds or duplicate toasts.
      try {
        OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event) => {
          try {
            const n = event.notification || {};
            window.dispatchEvent(new CustomEvent('fieldz-push-foreground', {
              detail: {
                id: n.notificationId,
                title: n.title,
                body: n.body,
                data: n.additionalData || {},
                url: n.launchURL || (n.additionalData || {}).url,
              },
            }));
          } catch { /* ignore */ }
        });
      } catch (fwdErr) {
        console.warn('foregroundWillDisplay listener not supported:', fwdErr);
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Failed to initialize OneSignal:', error);
      setIsLoading(false);
      // Fallback: check backend for subscription status
      await fetchSubscriptionStatus();
    }
  }, [isInitialized, user, fetchConfig, isNativeApp, fetchSubscriptionStatus]);

  // Store subscription on backend
  const storeSubscription = async (playerId) => {
    try {
      const token = getAuthToken();
      await fetch(`${API}/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          onesignal_player_id: playerId,
          is_subscribed: true,
          browser_type: navigator.userAgent.includes('Chrome') ? 'chrome' : 
                        navigator.userAgent.includes('Firefox') ? 'firefox' : 
                        navigator.userAgent.includes('Safari') ? 'safari' : 'other',
          device_type: 'web',
        }),
      });
    } catch (error) {
      console.error('Failed to store subscription:', error);
    }
  };

  // Subscribe to push notifications
  const subscribe = async () => {
    // In native app, push is managed by the native shell
    if (isNativeApp || window.FieldzNative?.isNativeApp) {
      // Request push permission via native bridge
      if (window.FieldzNative?.requestPushPermission) {
        window.FieldzNative.requestPushPermission();
      }
      // Check backend status after a delay
      setTimeout(async () => {
        await fetchSubscriptionStatus();
        toast.success('Push notifications enabled via mobile app!');
      }, 2000);
      return true;
    }

    if (!isInitialized) {
      await initializeOneSignal();
      if (!isInitialized) {
        toast.error('Push notifications require HTTPS. Please try on the production site.');
        return false;
      }
    }

    try {
      setIsLoading(true);
      await OneSignal.Notifications.requestPermission();
      const currentPermission = await OneSignal.Notifications.permission;
      setPermission(currentPermission ? 'granted' : 'denied');

      if (currentPermission) {
        await OneSignal.User.PushSubscription.optIn();
        setIsSubscribed(true);
        toast.success('Push notifications enabled!');
        return true;
      } else {
        toast.error('Push notification permission denied');
        return false;
      }
    } catch (error) {
      console.error('Failed to subscribe:', error);
      toast.error('Failed to enable push notifications');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Unsubscribe from push notifications
  const unsubscribe = async () => {
    if (!isInitialized) return false;

    try {
      setIsLoading(true);

      // In native app, just update backend
      if (isNativeApp || window.FieldzNative?.isNativeApp) {
        const token = getAuthToken();
        await fetch(`${API}/push/subscribe`, {
          method: 'DELETE',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
        });
        setIsSubscribed(false);
        toast.success('Push notifications disabled');
        return true;
      }

      await OneSignal.User.PushSubscription.optOut();
      setIsSubscribed(false);
      
      // Update backend
      const token = getAuthToken();
      await fetch(`${API}/push/subscribe`, {
        method: 'DELETE',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
      });
      
      toast.success('Push notifications disabled');
      return true;
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      toast.error('Failed to disable push notifications');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Re-register this device (fixes stale OneSignal/Expo registrations)
  const refreshRegistration = async () => {
    try {
      if (isNativeApp || window.FieldzNative?.isNativeApp) {
        if (window.FieldzNative?.expoPushToken) {
          await registerExpoToken(window.FieldzNative.expoPushToken);
        }
        await fetchSubscriptionStatus();
        toast.success('Push registration refreshed');
        return true;
      }
      if (user?.user_id) await OneSignal.login(user.user_id);
      const sub = await OneSignal.User.PushSubscription;
      if (sub?.id) await storeSubscription(sub.id);
      await fetchSubscriptionStatus();
      toast.success('Push registration refreshed');
      return true;
    } catch (error) {
      console.error('Failed to refresh registration:', error);
      toast.error('Couldn\'t refresh registration. Enable notifications for this site in your browser settings, then try again.');
      return false;
    }
  };

  // Send a test notification
  const sendTestNotification = async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API}/push/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        const onesignalOk = data.onesignal?.success && !data.onesignal?.data?.errors?.length;
        const expoOk = data.expo?.success;

        if (onesignalOk || expoOk) {
          toast.success('Test notification sent! Check your device.');
        } else if (data.onesignal?.data?.errors?.length) {
          toast.info('Notification sent, but no subscribed devices found. Make sure push is enabled on your device.');
        } else {
          toast.info('Notification dispatched. If you don\'t receive it, enable push notifications in your device settings.');
        }
        return true;
      } else {
        toast.error('Failed to send test notification');
        return false;
      }
    } catch (error) {
      console.error('Failed to send test notification:', error);
      toast.error('Failed to send test notification');
      return false;
    }
  };

  // Initialize on mount
  useEffect(() => {
    initializeOneSignal();
  }, [initializeOneSignal]);

  // Update external user ID when user changes (web only)
  useEffect(() => {
    if (isInitialized && user?.user_id && !isNativeApp && !window.FieldzNative?.isNativeApp) {
      OneSignal.login(user.user_id).catch(console.error);
    }
  }, [isInitialized, user, isNativeApp]);

  const value = {
    isInitialized,
    isSubscribed,
    isLoading,
    permission,
    subscribe,
    unsubscribe,
    sendTestNotification,
    refreshRegistration,
    isConfigured: onesignalConfig?.configured || false,
  };

  return (
    <PushNotificationContext.Provider value={value}>
      {children}
    </PushNotificationContext.Provider>
  );
}

export default PushNotificationProvider;
